// src/core/data/useFacilityWorkers.js

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  onSnapshot,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  normalizeId,
  normalizePlatformRole,
  PLATFORM_ROLES,
} from "../../security/permissions";

/*
 * ShiftBridge Canonical Facility Workforce Hook
 *
 * ARCHITECTURE
 * ------------
 *
 * facilityProfiles/{facilityId}/workers/{uid}
 *        ↓
 * facility-scoped roster / membership index
 *        ↓
 * users/{uid}
 *        ↓
 * canonical worker profile
 *
 * This replaces the previous five global users-collection queries.
 *
 * WHY
 * ---
 * Firestore list rules are not filters.
 *
 * A Planner should discover workers through a facility-scoped path,
 * not by querying the global users collection using several optional
 * legacy membership fields.
 */

/* =========================================================
   HELPERS
   ========================================================= */

function workerRoleIsValid(user = {}) {
  const normalizedRole =
    normalizePlatformRole(
      user?.platformRole ||
        user?.role ||
        ""
    );

  if (
    normalizedRole ===
    PLATFORM_ROLES.WORKER
  ) {
    return true;
  }

  /*
   * Temporary compatibility for older ShiftBridge
   * worker profile role values.
   */
  const legacyRole =
    normalizeId(user?.role);

  return [
    "nurse",
    "caregiver",
  ].includes(legacyRole);
}

function isArchived(user = {}) {
  return (
    user?.archived === true ||
    normalizeId(user?.status) ===
      "archived"
  );
}

function normalizeRosterRow(
  snapshotDoc
) {
  const data =
    snapshotDoc.data() || {};

  return {
    id: snapshotDoc.id,

    uid:
      String(
        data?.uid ||
          snapshotDoc.id ||
          ""
      ).trim(),

    ...data,
  };
}

function normalizeUserRow(
  snapshotDoc,
  rosterRow = {}
) {
  const data =
    snapshotDoc.data() || {};

  return {
    /*
     * Roster metadata comes first.
     * Canonical users/{uid} fields override it.
     */
    ...rosterRow,
    ...data,

    id: snapshotDoc.id,
    uid: snapshotDoc.id,

    rosterFacilityId:
      normalizeId(
        rosterRow?.facilityId ||
          ""
      ),
  };
}

/* =========================================================
   MAIN HOOK
   ========================================================= */

export default function useFacilityWorkers({
  tenantId,
  facilityId,
  enabled = true,
} = {}) {
  const normalizedTenantId =
    useMemo(
      () => normalizeId(tenantId),
      [tenantId]
    );

  const normalizedFacilityId =
    useMemo(
      () => normalizeId(facilityId),
      [facilityId]
    );

  const [workers, setWorkers] =
    useState([]);

  const [
    facilityUsers,
    setFacilityUsers,
  ] = useState([]);

  const [roster, setRoster] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [
    rosterLoading,
    setRosterLoading,
  ] = useState(false);

  const [
    profileLoading,
    setProfileLoading,
  ] = useState(false);

  /* =======================================================
     FACILITY ROSTER LISTENER
     ======================================================= */

  useEffect(() => {
    if (
      !enabled ||
      !normalizedTenantId ||
      !normalizedFacilityId
    ) {
      setWorkers([]);
      setFacilityUsers([]);
      setRoster([]);

      setLoading(false);
      setRosterLoading(false);
      setProfileLoading(false);

      setError(null);

      return undefined;
    }

    let active = true;

    let profileUnsubscribers = [];

    const profileRows =
      new Map();

    setLoading(true);
    setRosterLoading(true);
    setProfileLoading(false);

    setError(null);

    setWorkers([]);
    setFacilityUsers([]);
    setRoster([]);

    /* =====================================================
       PROFILE PUBLISHER
       ===================================================== */

    function publishProfiles(
      rosterRows
    ) {
      if (!active) {
        return;
      }

      const activeRosterUids =
        new Set(
          rosterRows
            .map((row) =>
              String(
                row?.uid || ""
              ).trim()
            )
            .filter(Boolean)
        );

      /*
       * Remove stale profiles if roster membership changes.
       */
      for (
        const uid of
        profileRows.keys()
      ) {
        if (
          !activeRosterUids.has(uid)
        ) {
          profileRows.delete(uid);
        }
      }

      const mergedRows =
        Array.from(
          profileRows.values()
        ).filter(Boolean);

        const scopedUsers =
  mergedRows.filter(
    (user) => {
      const uid =
        String(
          user?.uid ||
            user?.id ||
            ""
        ).trim();

      const email =
        String(
          user?.email ||
            ""
        ).trim();

      if (isArchived(user)) {
        console.warn(
          "PLANNER WORKER EXCLUDED — ARCHIVED",
          {
            uid,
            email,
            archived:
              user?.archived,
            status:
              user?.status,
          }
        );

        return false;
      }

      const userTenantId =
        normalizeId(
          user?.tenantId ||
            user?.tenantKey ||
            user?.tenantName ||
            ""
        );

      if (
        userTenantId !==
        normalizedTenantId
      ) {
        console.warn(
          "PLANNER WORKER EXCLUDED — TENANT MISMATCH",
          {
            uid,
            email,
            expectedTenantId:
              normalizedTenantId,
            actualTenantId:
              userTenantId,
            rawTenantId:
              user?.tenantId,
            rawTenantKey:
              user?.tenantKey,
            rawTenantName:
              user?.tenantName,
          }
        );

        return false;
      }

      return true;
    }
  );

      const scopedWorkers =
        scopedUsers.filter(
          workerRoleIsValid
        );

      setFacilityUsers(
        scopedUsers
      );

      setWorkers(
        scopedWorkers
      );

      console.log(
        "PLANNER FACILITY ROSTER WORKERS",
        {
          tenantId:
            normalizedTenantId,

          facilityId:
            normalizedFacilityId,

          rosterCount:
            rosterRows.length,

          profileCount:
            scopedUsers.length,

          workers:
            scopedWorkers.length,
        }
      );
    }

    /* =====================================================
       ROSTER LISTENER
       ===================================================== */

    const rosterRef =
      collection(
        db,
        "facilityProfiles",
        normalizedFacilityId,
        "workers"
      );

    const unsubscribeRoster =
      onSnapshot(
        rosterRef,

        (snapshot) => {
          if (!active) {
            return;
          }

          const rosterRows =
            snapshot.docs
              .map(
                normalizeRosterRow
              )
              .filter(
                (row) =>
                  Boolean(row.uid)
              );

          /*
           * Defensive tenant check.
           *
           * Older roster rows may not contain tenantId.
           * The facility document path is already authoritative
           * for facility membership; tenant authorization is
           * enforced by Firestore rules.
           */
          const acceptedRosterRows =
            rosterRows.filter(
              (row) => {
                const rowTenantId =
                  normalizeId(
                    row?.tenantId ||
                      ""
                  );

                return (
                  !rowTenantId ||
                  rowTenantId ===
                    normalizedTenantId
                );
              }
            );

          setRoster(
            acceptedRosterRows
          );

          setRosterLoading(false);

          /* ===============================================
             RESET EXISTING USER LISTENERS
             =============================================== */

          for (
            const unsubscribe of
            profileUnsubscribers
          ) {
            try {
              unsubscribe();
            } catch (cleanupError) {
              console.warn(
                "Facility worker profile listener cleanup failed.",
                cleanupError
              );
            }
          }

          profileUnsubscribers = [];

          profileRows.clear();

          if (
            acceptedRosterRows.length ===
            0
          ) {
            setWorkers([]);
            setFacilityUsers([]);

            setProfileLoading(false);
            setLoading(false);

            console.log(
              "PLANNER FACILITY ROSTER EMPTY",
              {
                tenantId:
                  normalizedTenantId,

                facilityId:
                  normalizedFacilityId,
              }
            );

            return;
          }

          setProfileLoading(true);

          let settledProfiles = 0;

          function markProfileSettled() {
            settledProfiles += 1;

            publishProfiles(
              acceptedRosterRows
            );

            if (
              settledProfiles >=
              acceptedRosterRows.length
            ) {
              setProfileLoading(false);
              setLoading(false);
            }
          }

          /* ===============================================
             INDIVIDUAL CANONICAL USER LISTENERS
             =============================================== */

          for (
            const rosterRow of
            acceptedRosterRows
          ) {
            const uid =
              String(
                rosterRow?.uid || ""
              ).trim();

            if (!uid) {
              markProfileSettled();
              continue;
            }

            const userRef =
              doc(
                db,
                "users",
                uid
              );

            let firstResult = true;

            const unsubscribeProfile =
              onSnapshot(
                userRef,

                (userSnapshot) => {
                  if (!active) {
                    return;
                  }

                  if (
                    userSnapshot.exists()
                  ) {
                    profileRows.set(
                      uid,
                      normalizeUserRow(
                        userSnapshot,
                        rosterRow
                      )
                    );
                  } else {
                    /*
                     * A stale roster entry should not create a
                     * fake worker profile.
                     */
                    profileRows.delete(
                      uid
                    );

                    console.warn(
                      "Facility roster points to missing user profile.",
                      {
                        tenantId:
                          normalizedTenantId,

                        facilityId:
                          normalizedFacilityId,

                        uid,
                      }
                    );
                  }

                  publishProfiles(
                    acceptedRosterRows
                  );

                  if (firstResult) {
                    firstResult = false;
                    markProfileSettled();
                  }
                },

                (profileError) => {
                  if (!active) {
                    return;
                  }

                  profileRows.delete(
                    uid
                  );

                  console.error(
                    "PLANNER FACILITY WORKER PROFILE ERROR",
                    {
                      tenantId:
                        normalizedTenantId,

                      facilityId:
                        normalizedFacilityId,

                      uid,

                      code:
                        profileError?.code,

                      message:
                        profileError?.message,
                    }
                  );

                  if (firstResult) {
                    firstResult = false;
                    markProfileSettled();
                  }
                }
              );

            profileUnsubscribers.push(
              unsubscribeProfile
            );
          }
        },

        (rosterError) => {
          if (!active) {
            return;
          }

          console.error(
            "PLANNER FACILITY ROSTER ERROR",
            {
              tenantId:
                normalizedTenantId,

              facilityId:
                normalizedFacilityId,

              code:
                rosterError?.code,

              message:
                rosterError?.message,
            }
          );

          setError(
            rosterError
          );

          setRoster([]);
          setWorkers([]);
          setFacilityUsers([]);

          setRosterLoading(false);
          setProfileLoading(false);
          setLoading(false);
        }
      );

    /* =====================================================
       CLEANUP
       ===================================================== */

    return () => {
      active = false;

      try {
        unsubscribeRoster();
      } catch (cleanupError) {
        console.warn(
          "Facility roster listener cleanup failed.",
          cleanupError
        );
      }

      for (
        const unsubscribe of
        profileUnsubscribers
      ) {
        try {
          unsubscribe();
        } catch (cleanupError) {
          console.warn(
            "Facility worker listener cleanup failed.",
            cleanupError
          );
        }
      }

      profileUnsubscribers = [];
      profileRows.clear();
    };
  }, [
    enabled,
    normalizedTenantId,
    normalizedFacilityId,
  ]);

  /* =======================================================
     RETURN CONTRACT
     ======================================================= */

  return {
    workers,

    /*
     * Kept because FacilityPlanner currently consumes this
     * compatibility field in several places.
     */
    facilityUsers,

    /*
     * Useful for diagnostics and later Workforce 360.
     */
    roster,

    loading,
    rosterLoading,
    profileLoading,

    error,

    tenantId:
      normalizedTenantId,

    facilityId:
      normalizedFacilityId,
  };
}